// Ticket prefix rules shared by Queue Setting (renaming an existing queue) and the
// New Queue dialog, so the client-side checks stay symmetric with the backend's
// POST / PUT /service_types validation instead of drifting apart.
//
// v2 §3.3: "Prefix shall support up to 2 alphabetic characters (i.e. P or PA)" —
// digits are no longer accepted.
export const PREFIX_PATTERN = /^[A-Za-z]{1,2}$/

// The built-in in-memory queue buckets, which exist independently of any service type:
// U = Unavailable, D = Done, X = Processing, T = Trash, Z = Calling History
// (ticketing-backend routes/api/queues/constants.js). A service type routed onto one of
// these names would merge with that system queue, so the backend rejects them too.
export const RESERVED_PREFIXES = ['D', 'U', 'X', 'T', 'Z']

export const PREFIX_FORMAT_MESSAGE = 'Prefix must be 1-2 letters (A-Z).'

// Ticket codes are the prefix concatenated verbatim with the running number, and every
// seeded prefix is upper case. Normalise on every write path (new queue and inline rename)
// so a lower-case entry cannot produce `ra001` in the kiosk, TV and SMS output.
export function normalizePrefix(prefix) {
  return String(prefix == null ? '' : prefix)
    .trim()
    .toUpperCase()
}

export function isPrefixValid(prefix) {
  return PREFIX_PATTERN.test(String(prefix == null ? '' : prefix).trim())
}

export function isPrefixReserved(prefix) {
  return RESERVED_PREFIXES.includes(
    String(prefix == null ? '' : prefix)
      .trim()
      .toUpperCase()
  )
}

// Returns an error message, or null when the prefix is acceptable.
// `queues` is the current service type list; `service_type_id` is excluded from the
// uniqueness check so an existing queue does not clash with itself.
export function validatePrefix(prefix, queues = [], service_type_id = null) {
  const trimmed = String(prefix == null ? '' : prefix).trim()
  if (!trimmed) return 'Prefix is required.'
  if (!isPrefixValid(trimmed)) return PREFIX_FORMAT_MESSAGE
  if (isPrefixReserved(trimmed)) return `Prefix "${trimmed.toUpperCase()}" is reserved for system queues.`

  const normalized = trimmed.toLowerCase()
  const taken = queues.some(queue => queue.service_type_id !== service_type_id && String(queue.prefix).toLowerCase() === normalized)
  if (taken) return `Prefix "${trimmed}" is already used by another queue.`

  return null
}
